// CRM BL: seletor de modelo do Super Admin › Settings › AI Assist (modelo de chat,
// de embeddings e do classificador). Mesmo desenho do ModelDropdown do Chatwoot
// (dashboard/routes/dashboard/settings/captain/components/ModelDropdown.vue):
// botão com chevron e popover claro/escuro com busca fixa no topo, ícone do
// fornecedor, selo "Preferido" e check no selecionado.
// O <input> original continua no formulário (vira hidden) e é ele que vai no submit.

export const MAX_RENDERED = 200;

const VENDORS = [
  {
    key: 'openai',
    label: 'OpenAI',
    icon: 'i-ri-openai-fill',
    match:
      /^(openai\/|gpt|o[1-9](-|$)|chatgpt|text-embedding-(3|ada)|davinci|babbage|whisper|dall-e|tts-)/,
  },
  {
    key: 'anthropic',
    label: 'Anthropic',
    icon: 'i-ri-anthropic-line',
    match: /^(anthropic\/|claude)/,
  },
  {
    key: 'google',
    label: 'Google',
    icon: 'i-ri-gemini-fill',
    match:
      /^(google\/|gemini|gemma|models\/gemini|text-embedding-00|embedding-00)/,
  },
  {
    key: 'meta',
    label: 'Meta',
    icon: 'i-ri-meta-fill',
    match: /^(meta-llama\/|meta\/|llama)/,
  },
  {
    key: 'mistral',
    label: 'Mistral',
    icon: 'i-logos-mistral-icon',
    match:
      /^(mistralai\/|mistral|mixtral|codestral|pixtral|ministral|magistral|devstral)/,
  },
  {
    key: 'deepseek',
    label: 'DeepSeek',
    icon: 'i-ri-deepseek-fill',
    match: /^(deepseek)/,
  },
  {
    key: 'xai',
    label: 'xAI',
    icon: 'i-ri-twitter-x-fill',
    match: /^(x-ai\/|xai\/|grok)/,
  },
  {
    key: 'qwen',
    label: 'Qwen',
    icon: 'i-ri-alibaba-cloud-fill',
    match: /^(qwen|alibaba\/)/,
  },
  {
    key: 'typesafe',
    label: 'TypeSafe',
    icon: 'i-lucide-shield-check',
    match: /^(typesafe\/|jev)/,
  },
];

const GENERIC_VENDOR = { key: 'other', label: '', icon: 'i-lucide-sparkles' };

export const vendorFor = id => {
  const value = (id || '').trim().toLowerCase();
  return VENDORS.find(vendor => vendor.match.test(value)) || GENERIC_VENDOR;
};

export const formatContext = length => {
  if (!length) return '';
  if (length >= 1000000) return `${Math.round(length / 100000) / 10}M ctx`;
  if (length >= 1000) return `${Math.round(length / 1000)}k ctx`;
  return `${length} ctx`;
};

export const modelDetails = model => {
  const parts = [];
  if (model.name && model.name !== model.id) parts.push(model.id);
  const context = formatContext(model.context_length);
  if (context) parts.push(context);
  if (model.embedding) parts.push('embeddings');
  return parts.join(' · ');
};

const scoreModel = (model, terms, query) => {
  const id = model.id.toLowerCase();
  const name = (model.name || '').toLowerCase();
  const haystack = `${id} ${name}`;
  if (!terms.every(term => haystack.includes(term))) return -1;
  if (id === query) return 0;
  if (id.startsWith(query) || id.split('/').pop().startsWith(query)) return 1;
  if (name.startsWith(query)) return 2;
  if (haystack.includes(query)) return 3;
  return 4;
};

// Filtra por todas as palavras digitadas (id ou nome), melhores primeiro,
// e corta em `limit` para a lista continuar leve com 500+ modelos.
export const filterModels = (models, rawQuery, limit = MAX_RENDERED) => {
  const query = (rawQuery || '').trim().toLowerCase();
  if (!query) return { items: models.slice(0, limit), total: models.length };
  const terms = query.split(/\s+/).filter(Boolean);
  const ranked = models
    .map((model, index) => ({
      model,
      index,
      score: scoreModel(model, terms, query),
    }))
    .filter(entry => entry.score >= 0)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(entry => entry.model);
  return { items: ranked.slice(0, limit), total: ranked.length };
};

const create = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const icon = (name, extra = '') =>
  create('span', `${name} shrink-0 size-4 ${extra}`.trim());

const ROW_CLASS =
  'flex items-center w-full gap-2 px-2 py-2 rounded-lg cursor-pointer select-none text-start text-n-slate-12';
const ROW_ACTIVE = ['bg-n-alpha-1', 'dark:bg-n-solid-3'];

// Abaixo desta largura o popover sai da coluna do campo e ocupa a tela
// (o Super Admin não recolhe a barra lateral no celular).
const NARROW_SCREEN = 640;

let pickerCount = 0;
let closeOpenPicker = null;

export const createModelPicker = (input, options = {}) => {
  const {
    searchPlaceholder = 'Buscar modelo…',
    emptyLabel = 'Padrão do provedor',
    noModelsText = 'Nenhum modelo carregado. Cole a chave e clique em "Carregar modelos", ou digite o id.',
    limit = MAX_RENDERED,
  } = options;

  pickerCount += 1;
  const uid = `${input.id || 'model'}-picker-${pickerCount}`;
  const state = {
    models: [],
    loading: false,
    open: false,
    query: '',
    active: 0,
    rows: [],
    defaultId: options.defaultId || '',
    savedId: input.value.trim(),
  };

  // Estrutura: wrapper relativo > botão + popover (busca fixa + lista rolável).
  const wrapper = create('div', 'relative mt-2');
  wrapper.dataset.modelPicker = '';

  const trigger = create(
    'button',
    'reset-base flex items-center justify-between w-full gap-2 px-3 py-2 min-h-[38px] text-sm !text-n-slate-12 rounded-lg border border-solid border-n-weak !bg-n-alpha-2 hover:!bg-n-alpha-1 dark:!bg-n-solid-2 dark:hover:!bg-n-solid-3 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand transition-colors'
  );
  trigger.type = 'button';
  trigger.id = `${uid}-trigger`;
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.dataset.modelPickerTrigger = '';
  const triggerIcon = create('span', 'hidden');
  const triggerText = create('span', 'flex-1 min-w-0 truncate text-start');
  const chevron = icon(
    'i-lucide-chevron-down',
    'text-n-slate-11 transition-transform'
  );
  trigger.append(triggerIcon, triggerText, chevron);

  const popover = create(
    'div',
    'hidden absolute z-50 start-0 end-0 top-full mt-1 flex flex-col min-w-0 max-h-[min(24rem,70vh)] overflow-hidden rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm'
  );
  popover.dataset.modelPickerPopover = '';

  const searchBox = create(
    'div',
    'relative shrink-0 p-2 border-0 border-b border-solid border-n-weak'
  );
  const searchIcon = icon(
    'i-lucide-search',
    '!size-3.5 absolute top-1/2 -translate-y-1/2 start-5 text-n-slate-10 pointer-events-none'
  );
  const search = create(
    'input',
    'reset-base block w-full !h-8 !m-0 !py-1.5 !ps-9 !pe-2 text-sm !text-n-slate-12 placeholder:text-n-slate-10 !border-0 !shadow-none rounded-lg !bg-n-alpha-black2 dark:!bg-n-solid-1 focus:!outline focus:!outline-1 focus:!outline-n-brand'
  );
  search.type = 'text';
  search.placeholder = searchPlaceholder;
  search.autocomplete = 'off';
  search.spellcheck = false;
  search.setAttribute('role', 'combobox');
  search.setAttribute('aria-autocomplete', 'list');
  search.setAttribute('aria-expanded', 'false');
  search.setAttribute('aria-controls', `${uid}-listbox`);
  search.setAttribute('aria-label', searchPlaceholder);
  search.dataset.modelPickerSearch = '';
  searchBox.append(searchIcon, search);

  const list = create(
    'ul',
    'reset-base flex flex-col gap-0.5 min-h-0 overflow-y-auto overscroll-contain !m-0 p-1.5 list-none'
  );
  list.id = `${uid}-listbox`;
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', 'Modelos');
  const footer = create(
    'div',
    'hidden shrink-0 px-3 py-2 text-xs text-n-slate-11 border-0 border-t border-solid border-n-weak'
  );
  footer.setAttribute('aria-live', 'polite');
  popover.append(searchBox, list, footer);
  wrapper.append(trigger, popover);

  input.insertAdjacentElement('beforebegin', wrapper);
  input.type = 'hidden';
  input.removeAttribute('list');
  const label = input.id && document.querySelector(`label[for="${input.id}"]`);
  if (label) {
    label.htmlFor = trigger.id;
    trigger.setAttribute('aria-label', label.textContent.trim());
  }

  const findModel = id => state.models.find(model => model.id === id);
  const preferredId = () => state.defaultId || state.savedId;
  // "jev-1.13.0" também marca "typesafe/jev-1.13.0" (ids do OpenRouter).
  const isPreferred = id => {
    const preferred = preferredId();
    return (
      Boolean(preferred) && (id === preferred || id.endsWith(`/${preferred}`))
    );
  };

  const renderTrigger = () => {
    const value = input.value.trim();
    if (value) {
      const model = findModel(value);
      triggerIcon.className = `${vendorFor(value).icon} shrink-0 size-4 text-n-slate-12`;
      triggerText.className =
        'flex-1 min-w-0 truncate text-start text-n-slate-12';
      triggerText.textContent = model?.name || value;
      trigger.title = '';
    } else {
      triggerIcon.className = 'hidden';
      triggerText.className =
        'flex-1 min-w-0 truncate text-start text-n-slate-10';
      triggerText.textContent = state.defaultId
        ? `${emptyLabel} (${state.defaultId})`
        : emptyLabel;
    }
  };

  const setActive = (index, { scroll = true } = {}) => {
    if (!state.rows.length) {
      state.active = -1;
      search.removeAttribute('aria-activedescendant');
      return;
    }
    const next = Math.max(0, Math.min(index, state.rows.length - 1));
    state.rows.forEach((row, rowIndex) =>
      ROW_ACTIVE.forEach(name =>
        row.element.classList.toggle(name, rowIndex === next)
      )
    );
    state.active = next;
    const element = state.rows[next].element;
    search.setAttribute('aria-activedescendant', element.id);
    if (scroll) element.scrollIntoView?.({ block: 'nearest' });
  };

  const buildRow = (index, { title, subtitle, iconName, value, badge }) => {
    const selected = value === input.value.trim();
    const row = create('li', ROW_CLASS);
    row.id = `${uid}-option-${index}`;
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', selected ? 'true' : 'false');
    row.dataset.value = value;
    row.append(icon(iconName, 'text-n-slate-12 self-start mt-0.5'));
    const body = create('div', 'flex flex-col flex-1 min-w-0 gap-1');
    const head = create('div', 'flex items-center gap-2 min-w-0');
    head.append(
      create('span', 'min-w-0 truncate font-medium leading-tight', title)
    );
    if (badge)
      head.append(
        create(
          'span',
          'shrink-0 text-[10px] uppercase leading-none text-n-iris-11 border border-solid border-n-iris-10 rounded-lg px-1 py-0.5',
          badge
        )
      );
    body.append(head);
    if (subtitle)
      body.append(
        create(
          'span',
          'truncate text-xs leading-tight text-n-slate-11',
          subtitle
        )
      );
    row.append(body);
    if (selected) row.append(icon('i-lucide-check', 'text-n-slate-12'));
    return row;
  };

  const renderList = () => {
    const query = state.query.trim();
    const { items, total } = filterModels(state.models, query, limit);
    const rows = [];

    if (!query)
      rows.push({
        value: '',
        title: emptyLabel,
        subtitle: state.defaultId || 'Usa o modelo padrão do provedor',
        iconName: 'i-lucide-rotate-ccw',
      });
    items.forEach(model =>
      rows.push({
        value: model.id,
        title: model.name || model.id,
        subtitle: modelDetails(model),
        iconName: vendorFor(model.id).icon,
        badge: isPreferred(model.id) ? 'Preferido' : null,
      })
    );
    const exact = state.models.some(
      model => model.id.toLowerCase() === query.toLowerCase()
    );
    if (query && !exact && !/\s/.test(query))
      rows.push({
        value: query,
        title: `Usar "${query}"`,
        subtitle: 'Id digitado, fora da lista',
        iconName: 'i-lucide-corner-down-left',
        custom: true,
      });

    const elements = rows.map((row, index) => buildRow(index, row));
    const status = [];
    if (state.loading) {
      const loading = create(
        'li',
        'flex items-center gap-2 px-2 py-3 text-n-slate-11'
      );
      loading.setAttribute('role', 'presentation');
      loading.append(
        icon('i-lucide-loader-circle', 'animate-spin text-n-slate-11'),
        create('span', '', 'Carregando modelos…')
      );
      status.push(loading);
    } else if (!state.models.length || (query && !items.length)) {
      const empty = create('li', 'px-2 py-3 text-xs text-n-slate-11');
      empty.setAttribute('role', 'presentation');
      empty.textContent = state.models.length
        ? 'Nenhum modelo encontrado com esse nome.'
        : noModelsText;
      status.push(empty);
    }
    list.replaceChildren(...status, ...elements);
    state.rows = rows.map((row, index) => ({
      ...row,
      element: elements[index],
    }));

    if (total > items.length) {
      footer.textContent = `Mostrando ${items.length} de ${total}. Continue digitando para refinar.`;
      footer.classList.remove('hidden');
    } else {
      footer.classList.add('hidden');
    }

    const selectedIndex = query
      ? 0
      : state.rows.findIndex(row => row.value === input.value.trim());
    setActive(selectedIndex >= 0 ? selectedIndex : 0, { scroll: !query });
  };

  const placePopover = () => {
    const rect = trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const upward = below < 280 && rect.top > below;
    const narrow = window.innerWidth < NARROW_SCREEN;
    popover.classList.toggle('top-full', !upward && !narrow);
    popover.classList.toggle('mt-1', !upward && !narrow);
    popover.classList.toggle('bottom-full', upward && !narrow);
    popover.classList.toggle('mb-1', upward && !narrow);
    popover.classList.toggle('!fixed', narrow);
    popover.classList.toggle('!inset-x-4', narrow);
    if (narrow) {
      popover.style.top = upward ? '' : `${rect.bottom + 4}px`;
      popover.style.bottom = upward
        ? `${window.innerHeight - rect.top + 4}px`
        : '';
    } else {
      popover.style.top = '';
      popover.style.bottom = '';
    }
  };

  const onOutside = event => {
    if (!wrapper.contains(event.target)) close(); // eslint-disable-line no-use-before-define
  };

  function close({ focusTrigger = false } = {}) {
    if (!state.open) return;
    state.open = false;
    popover.classList.add('hidden');
    chevron.classList.remove('rotate-180');
    trigger.setAttribute('aria-expanded', 'false');
    search.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onOutside, true);
    window.removeEventListener('resize', placePopover);
    window.removeEventListener('scroll', placePopover, true);
    if (closeOpenPicker === close) closeOpenPicker = null;
    if (focusTrigger) trigger.focus();
  }

  const open = (seed = '') => {
    if (state.open) return;
    if (closeOpenPicker) closeOpenPicker();
    closeOpenPicker = close;
    state.open = true;
    state.query = seed;
    search.value = seed;
    placePopover();
    popover.classList.remove('hidden');
    chevron.classList.add('rotate-180');
    trigger.setAttribute('aria-expanded', 'true');
    search.setAttribute('aria-expanded', 'true');
    renderList();
    document.addEventListener('pointerdown', onOutside, true);
    window.addEventListener('resize', placePopover);
    window.addEventListener('scroll', placePopover, true);
    search.focus({ preventScroll: true });
  };

  const setValue = (value, { emit = true } = {}) => {
    input.value = (value || '').trim();
    renderTrigger();
    if (state.open) renderList();
    if (emit) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const choose = index => {
    const row = state.rows[index];
    if (!row) return;
    setValue(row.value);
    close({ focusTrigger: true });
  };

  trigger.addEventListener('click', () => (state.open ? close() : open()));
  trigger.addEventListener('keydown', event => {
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      open();
    } else if (
      event.key.length === 1 &&
      event.key !== ' ' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      open(event.key);
    }
  });

  search.addEventListener('input', () => {
    state.query = search.value;
    renderList();
  });
  search.addEventListener('keydown', event => {
    const pageSize = 8;
    const moves = {
      ArrowDown: 1,
      ArrowUp: -1,
      PageDown: pageSize,
      PageUp: -pageSize,
    };
    if (moves[event.key]) {
      event.preventDefault();
      setActive(state.active + moves[event.key]);
    } else if (event.key === 'Home' && event.ctrlKey) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End' && event.ctrlKey) {
      event.preventDefault();
      setActive(state.rows.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(state.active);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close({ focusTrigger: true });
    } else if (event.key === 'Tab') {
      close();
    }
  });

  list.addEventListener('mousedown', event => event.preventDefault());
  list.addEventListener('pointermove', event => {
    const row = event.target.closest('[role="option"]');
    if (!row) return;
    const index = state.rows.findIndex(item => item.element === row);
    if (index >= 0 && index !== state.active)
      setActive(index, { scroll: false });
  });
  list.addEventListener('click', event => {
    const row = event.target.closest('[role="option"]');
    if (!row) return;
    choose(state.rows.findIndex(item => item.element === row));
  });

  renderTrigger();

  return {
    element: wrapper,
    trigger,
    open,
    close,
    setValue,
    getValue: () => input.value.trim(),
    setModels(models) {
      state.models = Array.isArray(models) ? models : [];
      state.loading = false;
      renderTrigger();
      if (state.open) renderList();
    },
    setLoading(loading) {
      state.loading = Boolean(loading);
      if (state.open) renderList();
    },
    setDefault(id) {
      state.defaultId = id || '';
      renderTrigger();
      if (state.open) renderList();
    },
    hasModel: id => Boolean(findModel(id)),
  };
};

export default createModelPicker;
